import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient, queryClient } from '@/lib';
import {
  ExperienceImageTypes,
  type RemoveImageForExperienceInput,
} from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import { usePageExperienceContext } from '../contexts/PageExperienceContext';
import type { PageExperience } from '../types';
import type {
  RemoveImageForExperienceMutation,
  RemoveImageForExperienceMutationVariables,
} from './useRemoveImageForExperience.generated';

type MutateFn = (
  options?: MutateOptions<
    RemoveImageForExperienceMutation,
    QueryError,
    RemoveImageForExperienceMutationVariables
  >
) => void;

type ImageTarget = Pick<
  PageExperience,
  'id' | 'heroImageUrl' | 'logoImageUrl' | 'bannerImageUrl'
>;

const query = gql`
  mutation RemoveImageForExperience($input: RemoveImageForExperienceInput!) {
    removeImageForExperience(input: $input)
  }
`;

/**
 * Removes from the loaded experience unless `target` names another one — the
 * planner header's logo belongs to the owning planner rather than the view the
 * author happens to be looking at, so that slot targets the parent. The target
 * carries its image URLs, not just an id, because the optimistic rollback
 * restores the value the removal cleared.
 */
export const useRemoveImageForExperience = (target?: ImageTarget) => {
  const { experience } = usePageExperienceContext();
  const targetExperience = target ?? experience;

  const { mutate, ...result } = useMutation<
    RemoveImageForExperienceMutation,
    QueryError,
    RemoveImageForExperienceMutationVariables
  >({
    onMutate: (variables) => {
      const { type, experienceId } = variables.input;
      if (type === ExperienceImageTypes.Hero) {
        return {
          optimisticData: {
            id: experienceId,
            heroImageUrl: null,
          },
          rollbackData: {
            id: experienceId,
            heroImageUrl: targetExperience.heroImageUrl,
          },
        };
      }
      if (type === ExperienceImageTypes.Logo) {
        return {
          optimisticData: {
            id: experienceId,
            logoImageUrl: null,
          },
          rollbackData: {
            id: experienceId,
            logoImageUrl: targetExperience.logoImageUrl,
          },
        };
      }
      return {
        optimisticData: {
          id: experienceId,
          bannerImageUrl: null,
        },
        rollbackData: {
          id: experienceId,
          bannerImageUrl: targetExperience.bannerImageUrl,
        },
      };
    },
    mutationFn: (variables) => {
      return gqlClient.request<
        RemoveImageForExperienceMutation,
        RemoveImageForExperienceMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(targetExperience.id),
      });
    },
  });

  const removeAsset = (
    type: ExperienceImageTypes,
    options?: Parameters<MutateFn>[0]
  ) => {
    const input: RemoveImageForExperienceInput = {
      experienceId: targetExperience.id,
      type,
    };
    return mutate({ input }, options);
  };

  const removeBannerImage: MutateFn = (options) =>
    removeAsset(ExperienceImageTypes.Banner, options);

  const removeLogo: MutateFn = (options) =>
    removeAsset(ExperienceImageTypes.Logo, options);

  return {
    ...result,
    removeAsset,
    removeBannerImage,
    removeLogo,
  };
};
